import React, { useMemo } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip } from 'react-leaflet';
import { useTranslation } from 'react-i18next';
import mapPoints from '../data/mapPoints.json';

type MapPoint = {
  type: 'producteur' | 'consommateur';
  commune: string;
  postal: string;
  lat: number;
  lng: number;
};

const PRODUCER_COLOR = '#FCAE0A';
const CONSUMER_COLOR = '#00516F';

export default function CommunityMap() {
  const { t } = useTranslation();
  const points = mapPoints as MapPoint[];

  const center = useMemo<[number, number]>(() => {
    if (points.length === 0) return [50.8466, 4.3528];
    const sumLat = points.reduce((acc, p) => acc + p.lat, 0);
    const sumLng = points.reduce((acc, p) => acc + p.lng, 0);
    return [sumLat / points.length, sumLng / points.length];
  }, [points]);

  return (
    <div className="w-full">
      <div
        className="rounded-2xl overflow-hidden"
        style={{ border: '1px solid #e5e5e5', height: '500px' }}
      >
        <MapContainer
          center={center}
          zoom={12}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={false}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          />
          {points.map((point, idx) => (
            <CircleMarker
              key={idx}
              center={[point.lat, point.lng]}
              radius={5}
              pathOptions={{
                color: point.type === 'producteur' ? PRODUCER_COLOR : CONSUMER_COLOR,
                fillColor: point.type === 'producteur' ? PRODUCER_COLOR : CONSUMER_COLOR,
                fillOpacity: 0.8,
                weight: 1,
              }}
            >
              <Tooltip direction="top" offset={[0, -8]} opacity={1}>
                <span style={{ fontSize: '12px' }}>
                  {point.type === 'producteur' ? t('realisations.map.producer') : t('realisations.map.consumer')} — {point.commune} ({point.postal})
                </span>
              </Tooltip>
            </CircleMarker>
          ))}
        </MapContainer>
      </div>

      <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center">
        <div className="flex items-center gap-2">
          <span
            className="inline-block w-3 h-3 rounded-full"
            style={{ backgroundColor: PRODUCER_COLOR }}
          />
          <span className="text-sm font-sans" style={{ color: '#1a1a1a' }}>{t('realisations.map.producer')}</span>
        </div>
        <span className="text-sm font-sans" style={{ color: '#666666' }}>·</span>
        <div className="flex items-center gap-2">
          <span
            className="inline-block w-3 h-3 rounded-full"
            style={{ backgroundColor: CONSUMER_COLOR }}
          />
          <span className="text-sm font-sans" style={{ color: '#1a1a1a' }}>{t('realisations.map.consumer')}</span>
        </div>
        <span className="text-sm font-sans" style={{ color: '#666666' }}>
          {t('realisations.map.privacy')}
        </span>
      </div>
    </div>
  );
}
